/**
 * Login form component
 */
import { authApi } from '@/api/auth';
import { usersApi } from '@/api/users';
import { useAuth } from '@/hooks/useAuth';
import { LoginRequest } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

interface LoginFormProps {
  onSwitchToRegister: () => void;
  /** Omit outer Paper and large headings (e.g. inside accordion) */
  compact?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToRegister,
  compact = false,
}) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginRequest) => {
    try {
      setError('');
      setIsLoading(true);

      // Use authApi to login
      console.log('[Login] Attempting to login with email:', data.email);
      const tokens = await authApi.login(data);
      console.log('[Login] Login successful, received tokens:', {
        hasAccessToken: !!tokens?.access_token,
        hasRefreshToken: !!tokens?.refresh_token,
      });

      // Tokens are now stored in httpOnly cookies by the server (if CORS allows)
      // We also store in localStorage for backward compatibility and fallback
      // This ensures Authorization header works even if cookies don't
      if (tokens && tokens.access_token) {
        localStorage.setItem('access_token', tokens.access_token);
        console.log('[Login] Saved access_token to localStorage');
      }
      if (tokens && tokens.refresh_token) {
        localStorage.setItem('refresh_token', tokens.refresh_token);
        console.log('[Login] Saved refresh_token to localStorage');
      }

      // Small delay to ensure tokens are saved and interceptor picks them up
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get user profile
      // Backend will try cookies first, then Authorization header
      // Since we set Authorization header in interceptor, it should work
      // Use usersApi.getProfile() instead of authApi.getCurrentUser() for consistency
      console.log('[Login] Fetching user profile...');
      const userProfile = await usersApi.getProfile();
      console.log('[Login] User profile fetched successfully:', userProfile.email);

      // Update auth state
      login(userProfile, tokens);

      const shouldOpenTour = localStorage.getItem('post_register_take_tour') === '1';
      if (shouldOpenTour) {
        localStorage.removeItem('post_register_take_tour');
        navigate('/app/home?tour=1');
      } else {
        navigate('/app/home');
      }

    } catch (err: any) {
      console.error('Login error:', err);
      // Extract error message from API error
      const errorMessage = err?.detail || err?.message || 'Login failed';
      setError(errorMessage);

      // Clear tokens on error
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } finally {
      setIsLoading(false);
    }
  };

  const shellSx = compact
    ? { pt: 0, pb: 1, maxWidth: 480, mx: 'auto' }
    : { p: 4, maxWidth: 400, mx: 'auto', mt: 8 };

  const inner = (
    <>
      {!compact && (
        <>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Login
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Sign in to your QuickField account
          </Typography>
        </>
      )}
      {compact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sign in to your QuickField account
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: compact ? 0 : 1 }}>
        <TextField
          {...register('email')}
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus={!compact}
          error={!!errors.email}
          helperText={errors.email?.message}
        />

        <TextField
          {...register('password')}
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="current-password"
          error={!!errors.password}
          helperText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        <Box textAlign="center">
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              onSwitchToRegister();
            }}
          >
            Don't have an account? Sign Up
          </Link>
        </Box>
      </Box>
    </>
  );

  if (compact) {
    return <Box sx={shellSx}>{inner}</Box>;
  }

  return (
    <Paper elevation={3} sx={shellSx}>
      {inner}
    </Paper>
  );
};
